import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Row, Col, Typography, Spin, Avatar, Progress, Button } from 'antd';
import { FireOutlined, TrophyOutlined, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';

let team1, team2;

try {

  const teams = require('../constants/teams');
  team1 = teams.team1;
  team2 = teams.team2;

} catch (error) {

  team1 = undefined;
  team2 = undefined;

}

const { Title, Text } = Typography;

const TeamRanking = () => {

  const [stats, setStats] = useState([]);
  const [callers, setCallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const hasTeams = team1 && team2 && team1.length > 0 && team2.length > 0;

  const fetchData = async () => {

    setLoading(true);
    
    try {

      const authToken = localStorage.getItem('authToken');

      const [statsRes, callersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/stats`, {
          headers: { Authorization: `Bearer ${authToken}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/callers`, {
          headers: { Authorization: `Bearer ${authToken}` }
        })
      ]);

      setStats(statsRes.data);
      setCallers(callersRes.data);

    } catch (error) {

      console.error('Error fetching data:', error);

    } finally {

      setLoading(false);

    }

  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let scrollInterval;
    if (isFullscreen) {
      const container = document.getElementById('fullscreen-content-container');
      if (container) {
        let scrollPosition = 0;
        let direction = 1; // 1 para abajo, -1 para arriba
        const scrollSpeed = 2; // píxeles por frame
        const pauseTime = 3000; // pausa en milisegundos al llegar al final/inicio
        let isPaused = false;
        
        scrollInterval = setInterval(() => {
          if (isPaused) return;
          
          const maxScroll = container.scrollHeight - container.clientHeight;
          
          if (direction === 1) {
            // Bajando
            if (scrollPosition >= maxScroll) {
              // Llegó al final, pausa y cambia dirección
              isPaused = true;
              setTimeout(() => {
                direction = -1;
                isPaused = false;
              }, pauseTime);
            } else {
              scrollPosition += scrollSpeed;
              container.scrollTop = scrollPosition;
            }
          } else {
            // Subiendo
            if (scrollPosition <= 0) {
              // Llegó al inicio, pausa y cambia dirección
              isPaused = true;
              setTimeout(() => {
                direction = 1;
                isPaused = false;
              }, pauseTime);
            } else {
              scrollPosition -= scrollSpeed;
              container.scrollTop = scrollPosition;
            }
          }
        }, 16); // ~60fps
      }
    }

    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [isFullscreen]);

  useEffect(() => {

    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isFullscreen]);

  const processData = (teamMembers) => {
    return teamMembers.map(member => {
      const callerData = callers.find(c => c.name === member);
      const memberStats = stats.filter(s => s.caller_name === member);
      const totalCalls = memberStats.reduce((sum, stat) => sum + stat.total_calls, 0);
      
      return {
        caller_name: member,
        calls: totalCalls,
        imageUrl: callerData?.imageUrl || `https://lioninstallations.com/Team%20Photos/${member.replace(/ /g, '%20')}.png`
      };
    }).sort((a, b) => b.calls - a.calls);
  };

  const processAllCallers = () => {
    const allCallerNames = [...new Set(stats.map(s => s.caller_name))];
    return processData(allCallerNames);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20%' }}>
        <Spin tip="Cargando ranking..." size="large" style={{ color: '#f49b33' }} />
      </div>
    );
  }

  const renderHeader = () => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center', 
      marginBottom: isFullscreen ? '20px' : '32px',
      padding: isFullscreen ? '0 20px' : '0',
      position: 'relative'
    }}>
      <Title level={1} style={{
        color: '#f49b33', 
        margin: 0,
        fontWeight: 700,
        fontSize: isFullscreen ? '48px' : '32px',
        textAlign: 'center'
      }}>
        {hasTeams ? (
          <><FireOutlined style={{ marginRight: '16px', fontSize: isFullscreen ? '48px' : '32px' }} />
          BATALLA DE LEONES</>
        ) : (
          <><TrophyOutlined style={{ marginRight: '16px', fontSize: isFullscreen ? '48px' : '32px' }} />
          RANKING DE LEONES</>
        )}
      </Title>
      <Button
        type="primary"
        icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        onClick={toggleFullscreen}
        size={isFullscreen ? "large" : "default"}
        style={{
          backgroundColor: '#f49b33',
          borderColor: '#f49b33',
          fontSize: isFullscreen ? '24px' : '16px',
          height: isFullscreen ? '50px' : '40px',
          width: isFullscreen ? '50px' : '40px',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          right: isFullscreen ? '20px' : '0',
          borderRadius: '8px'
        }}
      />
    </div>
  );

  const renderContent = () => {

    if (hasTeams) {

      const team1Data = processData(team1);
      const team2Data = processData(team2);
      const team1Total = team1Data.reduce((sum, item) => sum + item.calls, 0);
      const team2Total = team2Data.reduce((sum, item) => sum + item.calls, 0);

      return (
        <>
          {!isFullscreen && (
            <div style={{
              backgroundColor: '#252525',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '40px',
              border: '1px solid #333'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '24px'
              }}>
                <Text strong style={{ 
                  color: '#0d6efd', 
                  fontSize: '22px',
                  fontWeight: 600
                }}>
                  LION'S PATH
                </Text>
                <Text strong style={{ 
                  color: '#ff7900', 
                  fontSize: '22px',
                  fontWeight: 600
                }}>
                  GOLDEN PRIDE
                </Text>
              </div>

              <div style={{
                height: '60px',
                backgroundColor: '#2a2a2a',
                borderRadius: '30px',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <div style={{
                  height: '100%',
                  width: `${team1Total + team2Total > 0 ? (team1Total / (team1Total + team2Total)) * 100 : 50}%`,
                  background: 'linear-gradient(90deg, #0d6efd, #3399ff)',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  position: 'relative',
                  borderRadius: '30px 0 0 30px',
                  boxShadow: '3px 0 5px rgba(0, 0, 0, 0.3)',
                  zIndex: 2,
                  minWidth: '40px'
                }}>
                  <span style={{ 
                    fontSize: '40px',
                    transform: 'rotate(-30deg)',
                    position: 'absolute',
                    right: '-5px',
                    filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.8))',
                    zIndex: 3,
                    animation: 'punchLeft 0.4s infinite alternate'
                  }}>👊</span>
                </div>

                <div style={{
                  height: '100%',
                  width: `${team1Total + team2Total > 0 ? (team2Total / (team1Total + team2Total)) * 100 : 50}%`,
                  background: 'linear-gradient(90deg, #ff7900, #ffae42)',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  position: 'relative',
                  borderRadius: '0 30px 30px 0',
                  boxShadow: '-3px 0 5px rgba(0, 0, 0, 0.3)',
                  zIndex: 2,
                  minWidth: '40px'
                }}>
                  <span style={{ 
                    fontSize: '40px',
                    transform: 'rotate(30deg) scaleX(-1)',
                    position: 'absolute',
                    left: '-5px',
                    filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.8))',
                    zIndex: 3,
                    animation: 'punchRight 0.4s infinite alternate'
                  }}>👊</span>
                </div>
              </div>
            </div>
          )}

          <Row gutter={[isFullscreen ? 40 : 32, isFullscreen ? 40 : 32]}>
            <Col xs={24} md={12}>
              <TeamCard 
                teamName={
                  <Text strong style={{ 
                    color: '#0d6efd', 
                    fontSize: isFullscreen ? '32px' : '26px',
                    textAlign: 'center', 
                    width: '100%',
                    fontWeight: 600
                  }}>
                    LION'S PATH
                  </Text>
                } 
                data={team1Data} 
                color="#0d6efd"
                isFullscreen={isFullscreen}
              />
            </Col>

            <Col xs={24} md={12}>
              <TeamCard 
                teamName={
                  <Text strong style={{ 
                    color: '#ff7900', 
                    fontSize: isFullscreen ? '32px' : '26px',
                    textAlign: 'center', 
                    width: '100%',
                    fontWeight: 600
                  }}>
                    GOLDEN PRIDE
                  </Text>
                } 
                data={team2Data} 
                color="#ff7900"
                isFullscreen={isFullscreen}
              />
            </Col>
          </Row>
        </>
      );
    } else {
      const allCallersData = processAllCallers();
      
      return (
        <Row justify="center">
          <Col xs={24} lg={isFullscreen ? 24 : 16} xl={isFullscreen ? 20 : 12}>
            <UnifiedRankingCard data={allCallersData} isFullscreen={isFullscreen} />
          </Col>
        </Row>
      );
    }
  };

  const containerStyle = isFullscreen ? {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: '#1a1a1a',
    zIndex: 9999,
    padding: '20px',
    boxSizing: 'border-box'
  } : {
    padding: '24px', 
    backgroundColor: '#1a1a1a', 
    minHeight: '100vh'
  };

  return (
    <div style={containerStyle}>
      {/* Animaciones CSS */}
      <style>
        {`
          @keyframes punchLeft {
            0% { transform: rotate(-30deg) translateX(0); }
            100% { transform: rotate(-30deg) translateX(5px); }
          }
          @keyframes punchRight {
            0% { transform: rotate(30deg) scaleX(-1) translateX(0); }
            100% { transform: rotate(30deg) scaleX(-1) translateX(-5px); }
          }
        `}
      </style>

      {/* Header fijo */}
      {renderHeader()}
      
      {/* Contenido scrolleable */}
      <div 
        id="fullscreen-content-container"
        style={{
          height: isFullscreen ? 'calc(100vh - 120px)' : 'auto',
          overflowY: isFullscreen ? 'auto' : 'visible',
          paddingRight: isFullscreen ? '10px' : '0'
        }}
      >
        {renderContent()}
      </div>
    </div>
  );
};

const UnifiedRankingCard = ({ data, isFullscreen = false }) => {

  const maxCalls = Math.max(...data.map(item => item.calls), 1);

  return (
    <Card
      style={{
        backgroundColor: '#1e1e1e',
        borderRadius: '16px',
        border: 'none',
        height: '100%',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        overflow: 'hidden'
      }}
      headStyle={{
        borderBottom: '1px solid #333',
        backgroundColor: '#1a1a1a',
        textAlign: 'center',
        padding: '16px',
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px'
      }}
      bodyStyle={{ padding: '8px 0' }}
    >
      {data.map((item, index) => {

        let rankColor = '#f49b33';
        let rankGradient = 'linear-gradient(135deg, #f49b33,rgb(245, 167, 115))';
        
        if (index === 0) {

          rankColor = '#ffd700';
          rankGradient = 'linear-gradient(135deg, #ffd700, #ffb700)';

        } else if (index === 1) {

          rankColor = '#c0c0c0';
          rankGradient = 'linear-gradient(135deg, #e6e6e6, #c0c0c0)';

        } else if (index === 2) {

          rankColor = '#cd7f32';
          rankGradient = 'linear-gradient(135deg, #e3964a, #cd7f32)';

        }

        return (
          <div 
            key={item.caller_name}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '20px 24px',
              margin: '8px 12px',
              borderBottom: index < data.length - 1 ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
              backgroundColor: index < 3 ? 'rgba(255, 255, 255, 0.03)' : 'transparent',
              borderRadius: '12px',
              transition: 'all 0.2s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            className="ranking-item"
          >
            {index < 3 && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: `linear-gradient(90deg, transparent, ${rankColor}, transparent)`,
                opacity: 0.6
              }} />
            )}
            
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: rankGradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '20px',
              fontWeight: 'bold',
              color: index < 3 ? '#000' : '#fff',
              fontSize: '16px',
              boxShadow: index < 3 ? `0 0 10px ${rankColor}40` : 'none',
              flexShrink: 0
            }}>
              {index + 1}
            </div>

            <Avatar
              size={120}
              style={{
                marginRight: '20px',
                border: `3px solid ${rankColor}`,
                backgroundColor: '#2a2a2a',
                padding: 0,
                flexShrink: 0,
                boxShadow: `0 0 0 1px #2a2a2a, 0 4px 12px rgba(0, 0, 0, 0.4)`
              }}
            >
              <img
                src={item.imageUrl}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://lioninstallations.com/Team%20Photos/LEON.jpg';
                }}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover', 
                  borderRadius: '50%' 
                }}
                alt={item.caller_name}
              />
            </Avatar>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <Text strong style={{ 
                  color: '#fff', 
                  fontSize: '18px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginRight: '12px'
                }}>
                  {item.caller_name}
                </Text>
              </div>
              
              <Progress
                percent={(item.calls / maxCalls) * 100}
                strokeColor={{
                  '0%': rankColor,
                  '100%': rankColor
                }}
                showInfo={false}
                trailColor="#2d2d2d"
                style={{ 
                  width: '100%',
                  height: '12px',
                  borderRadius: '20px',
                  marginBottom: '4px'
                }}
                strokeWidth={12}
              />
            </div>
          </div>
        );
      })}
    </Card>
  );
};

const TeamCard = ({ teamName, data, color, isFullscreen = false }) => {

  const maxCalls = Math.max(...data.map(item => item.calls), 1);

  return (
    <Card
      title={<Text strong style={{ color }}>{teamName}</Text>}
      style={{
        backgroundColor: '#252525',
        borderRadius: '12px',
        border: isFullscreen ? '2px solid #333' : '1px solid #333',
        height: '100%'
      }}
      headStyle={{
        borderBottom: '1px solid #333',
        backgroundColor: '#1e1e1e'
      }}
      bodyStyle={{ padding: isFullscreen ? '20px 0' : '16px 0' }}
    >
      {data.map((item) => {
        const avatarSize = isFullscreen ? 140 : 100;
        const progressHeight = isFullscreen ? 40 : 30;
        const itemPadding = isFullscreen ? '16px 20px' : '12px 16px';

        return (
          <div 
            key={item.caller_name}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: itemPadding,
              borderBottom: '1px solid #333',
              minHeight: isFullscreen ? '160px' : 'auto'
            }}
          >
            <Avatar
              size={avatarSize}
              style={{
                marginRight: isFullscreen ? '24px' : '16px',
                border: `${isFullscreen ? 3 : 2}px solid ${color}`,
                backgroundColor: '#2a2a2a',
                padding: 0,
                flexShrink: 0
              }}
            >
              <img
                src={item.imageUrl}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://lioninstallations.com/Team%20Photos/LEON.jpg';
                }}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover', 
                  borderRadius: '50%' 
                }}
                alt={item.caller_name}
              />
            </Avatar>

            <div style={{ flex: 1 }}>
              {isFullscreen && (
                <div style={{
                  marginBottom: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Text strong style={{ 
                    color: '#fff', 
                    fontSize: '20px',
                    fontWeight: 600
                  }}>
                    {item.caller_name}
                  </Text>
                </div>
              )}
              
              <Progress
                percent={(item.calls / maxCalls) * 100}
                strokeColor={color}
                showInfo={false}
                trailColor="#353535"
                style={{ 
                  width: '100%',
                  height: `${progressHeight}px`,
                  borderRadius: '10px'
                }}
                strokeWidth={progressHeight}
              />
            </div>
          </div>
        );
      })}
    </Card>
  );
};

export default TeamRanking;